// src/components/auth/BusinessNameModal.jsx
//
// Shown to new businesses (created by switching from the personal app) so the
// owner can set a proper business name. The name is checked for availability
// (and CAC registration) before saving, then the user continues to market
// selection.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import { Building2, CheckCircle2, AlertCircle, X, Loader } from 'lucide-react';

const BusinessNameModal = ({ show, user, onComplete, onClose }) => {
  const [businessName, setBusinessName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | checking | available | taken | registered
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const checkTimeout = useRef(null);

  const checkAvailability = useCallback(async (name) => {
    if (!name || name.trim().length < 3) {
      setStatus('idle');
      setMessage('');
      return;
    }
    setStatus('checking');
    setMessage('');
    try {
      const { data } = await api.post(API_CONFIG.AUTH.IS_REGISTERED_BUSINESS, {
        businessName: name.trim(),
        userId: user?.company_id || user?.id
      });
      if (data.isRegistered) {
        setStatus('registered');
        setMessage('This business name is registered. You will be required to provide further details to use it.');
      } else if (data.isTaken || (data.error && data.exists)) {
        setStatus('taken');
        setMessage('This business name is already taken. Please choose another.');
      } else {
        setStatus('available');
        setMessage('Business name is available.');
      }
    } catch (error) {
      setStatus('idle');
      setMessage('');
    }
  }, [user?.company_id, user?.id]);

  useEffect(() => {
    if (!show) {
      setBusinessName('');
      setStatus('idle');
      setMessage('');
      return;
    }
    // Prefill from the account when it already has a name
    const initial = user?.company_name && user.company_name !== user?.fullname ? user.company_name : '';
    setBusinessName(initial || '');
  }, [show, user?.company_name, user?.fullname]);

  useEffect(() => {
    return () => {
      if (checkTimeout.current) clearTimeout(checkTimeout.current);
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setBusinessName(value);
    setStatus('idle');
    setMessage('');
    if (checkTimeout.current) clearTimeout(checkTimeout.current);
    if (value.trim().length >= 3) {
      checkTimeout.current = setTimeout(() => checkAvailability(value), 600);
    }
  };

  const canSubmit = businessName.trim().length >= 3 && (status === 'available' || status === 'idle') && !isSaving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    try {
      const submitData = new FormData();
      submitData.append('company', businessName.trim());
      submitData.append('fullname', user?.fullname || '');
      submitData.append('phonenumber', user?.company_phone || user?.phoneNumber || '');
      submitData.append('email', user?.company_email || user?.email || '');

      const { data } = await api.post(API_CONFIG.USER.UPDATE_PERSONAL_DETAILS, submitData);

      if (data.success) {
        showToast('Business name saved successfully', 'success');
        onComplete();
      } else {
        showToast(data.error || 'Failed to save business name', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save business name', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 animate-fade-in">
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-1">Name your business</h2>
        <p className="text-sm text-gray-500 mb-5">
          Give your business a name so customers can find you. You can change this later from settings.
        </p>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-gray-700">Business name</label>
          <div className="relative">
            <input
              type="text"
              value={businessName}
              onChange={handleChange}
              placeholder="e.g. Jane's Fashion Store"
              className={`w-full pl-11 pr-10 py-3.5 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all ${
                status === 'available' ? 'border-green-300 bg-green-50' :
                status === 'taken' || status === 'registered' ? 'border-red-300 bg-red-50' :
                'border-gray-200'
              }`}
            />
            <div className="absolute inset-y-0 left-3.5 flex items-center text-gray-400">
              <Building2 size={18} />
            </div>
            <div className="absolute inset-y-0 right-3.5 flex items-center">
              {status === 'checking' && <Loader size={18} className="text-blue-500 animate-spin" />}
              {status === 'available' && <CheckCircle2 size={18} className="text-green-500" />}
              {(status === 'taken' || status === 'registered') && <AlertCircle size={18} className="text-red-500" />}
            </div>
          </div>

          {message && (
            <p className={`text-xs mt-1 ${status === 'available' ? 'text-green-600' : 'text-red-600'}`}>{message}</p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full mt-6 py-3.5 text-base font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isSaving ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default BusinessNameModal;
